import { mapProductCompliance } from '~/Api/CzSuklCompliance';
import mapGeneralProduct from '~/Api/Product/mapProduct';
import { mapDataSource } from '~/Api/DataSource';
import { Country } from '~/Api/Country';
import ProductPrice from '~/Api/Product/ProductPrice';
import Price from '~/utils/Price/Price';
import {
	ErrorCode,
	CzMaxPrice,
	Error,
	FinalCalculation,
	Product,
	ProductComputedPrice,
	ProductDataSource,
	Reference,
	ReferencePrice,
} from '~/libs/infrastructure/schemas/czMaxPrice';
import {
	CzechMaxPriceCalculationItemPriceResponse,
	CzechMaxPriceCalculationItemSettingsResponse,
	CzechMaxPriceCalculationSettingsResponse,
	CzechMaxPriceFinalCalculationResponse,
	ErrorResource,
} from 'com.cogvio.pricemonitor.api';

function mapFinalCalculationError(error: ErrorResource): Error {
	const errorCode = (error.code ?? '') as ErrorCode;
	const { product, duplicateCountries } = (error.meta ?? {}) as {
		product?: { id: string };
		duplicateCountries?: { alpha2: string; name: string }[];
	};

	return new Error(errorCode, product?.id ?? null, duplicateCountries?.map((c) => new Country(c.alpha2, c.name)) ?? []);
}

function mapReferencePrice(referencePrice: CzechMaxPriceCalculationItemPriceResponse): ReferencePrice {
	const { sourcePrice, calculatedPrice } = referencePrice;

	return new ReferencePrice(
		new ProductPrice(sourcePrice.type, new Price(String(sourcePrice.value), sourcePrice.currency), 0),
		new ProductComputedPrice(
			calculatedPrice.type,
			new Price(String(calculatedPrice.value), calculatedPrice.currency),
			calculatedPrice.description,
			calculatedPrice.selected
		)
	);
}

function mapProduct(product: CzechMaxPriceCalculationItemSettingsResponse): Product {
	return new Product(
		mapGeneralProduct(product.product),
		new ProductDataSource(product.datasource.type, product.datasource.suklReference),
		product.referencePrices.map(mapReferencePrice),
		product.packageSizeForCalculation,
		product.packageSizeForSingleUnit,
		product.singleUnitPackageSize,
		product.selected,
		mapProductCompliance(product.productCompliance)
	);
}

function mapFinalCalculation(finalCalculation: CzechMaxPriceFinalCalculationResponse): FinalCalculation {
	const { secondAndThirdLowestPriceAvg, threeLowestPricesAvg, currency } = finalCalculation;

	return new FinalCalculation(
		currency,
		secondAndThirdLowestPriceAvg !== null && currency !== null
			? new Price(String(secondAndThirdLowestPriceAvg), currency)
			: null,
		threeLowestPricesAvg !== null && currency !== null ? new Price(String(threeLowestPricesAvg), currency) : null,
		finalCalculation.deviation,
		finalCalculation.emptyResultReason.map(mapFinalCalculationError),
		finalCalculation.fromProducts.map((product) => ({
			id: product.id,
			dataSource: mapDataSource(product.dataSource),
		}))
	);
}

export function mapCzMaxPrice(czMaxPrice: CzechMaxPriceCalculationSettingsResponse): CzMaxPrice {
	return new CzMaxPrice(
		new Reference(czMaxPrice.reference.packageSize, czMaxPrice.reference.referenceBasketVersion),
		czMaxPrice.products.map(mapProduct),
		mapFinalCalculation(czMaxPrice.finalCalculation),
		czMaxPrice._links
	);
}
